import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useConfirmMis } from "./useConfirmMis";
import {
  PrivilegeKind,
  TerminalMisConfigDto,
} from "../../types/Mis/MisTypes";
import { languageType } from "../../components/Main/TerminalModes/Normal/NormalMode/terminalModeShared";
import { TerminalMisScanTarget } from "../../components/Main/Modals/TerminalMisScanModal/TerminalMisScanModal";

export type MisTicketConfirmation = {
  confirmationId: string;
  privilegeTypeId?: number;
  privilegeKind: PrivilegeKind;
  expiresAtMs: number;
};

const bannerCopy = {
  BENEFIT: {
    ru: "Льгота подтверждена. Выберите услугу.",
    by: "Льгота пацверджана. Выберыце паслугу.",
    en: "Benefit confirmed. Select a service.",
  },
  EMERGENCY: {
    ru: "CITO подтверждено. Выберите услугу.",
    by: "CITO пацверджана. Выберыце паслугу.",
    en: "CITO confirmed. Select a service.",
  },
};

export const useMisTerminalSession = (
  language: languageType,
  misConfig?: TerminalMisConfigDto | null,
) => {
  const { confirmMisAsync, isConfirming } = useConfirmMis();
  const [scanTarget, setScanTarget] = useState<TerminalMisScanTarget | null>(
    null,
  );
  const [scanError, setScanError] = useState<string | null>(null);
  const [confirmation, setConfirmation] =
    useState<MisTicketConfirmation | null>(null);

  useEffect(() => {
    if (!confirmation) return;
    const remain = confirmation.expiresAtMs - Date.now();
    if (remain <= 0) {
      setConfirmation(null);
      return;
    }
    const timer = window.setTimeout(() => setConfirmation(null), remain);
    return () => window.clearTimeout(timer);
  }, [confirmation]);

  const openScan = useCallback((target: TerminalMisScanTarget) => {
    setScanError(null);
    setScanTarget(target);
  }, []);

  const closeScan = useCallback(() => {
    setScanTarget(null);
    setScanError(null);
  }, []);

  const submitScan = useCallback(
    async (value: string) => {
      if (!scanTarget) return null;
      const target = scanTarget;
      setScanError(null);
      try {
        const payload =
          target.scanCard || target.scanOnService
            ? { identifier: value, digitalCode: value }
            : {
                privilegeTypeId: target.privilegeTypeId,
                misCode: target.misCode,
                identifier: value,
                digitalCode: value,
              };
        const { data } = await confirmMisAsync(payload);
        const expiresAtMs = data.expiresAt
          ? new Date(data.expiresAt).getTime()
          : Date.now() + 3 * 60 * 1000;
        const nextConfirmation: MisTicketConfirmation = {
          confirmationId: data.confirmationId,
          privilegeTypeId: data.privilegeTypeId,
          privilegeKind: data.privilegeKind ?? target.privilegeKind,
          expiresAtMs,
        };
        if (!target.scanOnService) {
          setConfirmation(nextConfirmation);
        }
        setScanTarget(null);
        return { confirmation: nextConfirmation, target };
      } catch (error) {
        const message = axios.isAxiosError(error)
          ? String(error.response?.data?.message ?? "").trim()
          : "";
        setScanError(message || "Не удалось подтвердить льготу");
        return null;
      }
    },
    [confirmMisAsync, scanTarget],
  );

  const clearConfirmation = useCallback(() => setConfirmation(null), []);

  const privilegeBanner = useMemo(() => {
    if (!confirmation) return null;
    return bannerCopy[confirmation.privilegeKind][language];
  }, [confirmation, language]);

  const allowsDigitalCode = Boolean(misConfig?.otoAllowsDigitalCode);
  const allowsBarcode =
    scanTarget?.privilegeKind === "EMERGENCY"
      ? Boolean(misConfig?.otoAllowsBarcode)
      : true;
  const localTest = Boolean(misConfig?.localTest);

  return {
    scanTarget,
    scanError,
    isConfirming,
    confirmation,
    privilegeBanner,
    allowsDigitalCode,
    allowsBarcode,
    localTest,
    openScan,
    closeScan,
    submitScan,
    clearConfirmation,
  };
};
